import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, Building2, Users, Search } from 'lucide-react';

const EnhancedClientFilter = ({ 
  assignedClients = [], 
  allClients = [], 
  selectedClients = new Set(), 
  onSelectionChange,
  accessType = 'team_member',
  userRole = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm(''); // Clear search when closing
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Small delay to ensure the dropdown is rendered
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  // Organize clients by priority and apply search filter
  const organizedClients = React.useMemo(() => {
    const assignedSet = new Set(assignedClients);
    const searchLower = searchTerm.toLowerCase().trim();
    
    // Filter assigned clients by search term
    const assigned = assignedClients
      .filter(client => !searchLower || client.toLowerCase().includes(searchLower))
      .map(client => ({
        value: client,
        label: client,
        isAssigned: true
      }));
    
    // Filter other clients by search term
    const others = allClients
      .filter(client => !assignedSet.has(client))
      .filter(client => !searchLower || client.toLowerCase().includes(searchLower))
      .sort()
      .map(client => ({
        value: client,
        label: client,
        isAssigned: false
      }));
    
    return { assigned, others };
  }, [assignedClients, allClients, searchTerm]);

  const handleClientToggle = (clientValue) => {
    const newSelected = new Set(selectedClients);
    if (newSelected.has(clientValue)) {
      newSelected.delete(clientValue);
    } else {
      newSelected.add(clientValue);
    }
    onSelectionChange(newSelected);
  };

  const getDisplayText = () => {
    const count = selectedClients.size;
    if (count === 0) return 'Select Clients';
    if (count === 1) return Array.from(selectedClients)[0];
    return `${count} clients selected`;
  };

  const getAccessTypeIcon = () => {
    switch (accessType) {
      case 'admin':
        return <Users className="w-4 h-4 text-purple-600" />;
      case 'supervisor':
        return <Users className="w-4 h-4 text-blue-600" />;
      case 'team_lead':
        return <Building2 className="w-4 h-4 text-green-600" />;
      case 'account_manager':
        return <Building2 className="w-4 h-4 text-blue-600" />;
      case 'team_member':
        return <Building2 className="w-4 h-4 text-gray-600" />;
      default:
        return <Building2 className="w-4 h-4 text-gray-600" />;
    }
  };

  const getAccessTypeDescription = () => {
    switch (accessType) {
      case 'admin':
        return 'Full access to all clients';
      case 'supervisor':
        return `Supervisor access to all supervised team clients`;
      case 'team_lead':
        return `Team lead access to ${assignedClients.length} assigned clients`;
      case 'account_manager':
        return `Account Manager with ${assignedClients.length} assigned clients`;
      case 'team_member':
        return `Team member access via team lead (${assignedClients.length} clients)`;
      default:
        return 'No client assignments';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full min-w-[200px] px-3 py-2 text-left bg-white border rounded-lg 
          shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 
          focus:border-blue-500 transition-colors
          ${isOpen ? 'ring-2 ring-blue-500 border-blue-500' : 'border-gray-300'}
        `}
        data-testid="client-filter-button"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 min-w-0">
            {getAccessTypeIcon()}
            <span className="truncate text-sm">{getDisplayText()}</span>
          </div>
          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-hidden">
          {/* Header with access type info */}
          <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              {getAccessTypeIcon()}
              <div>
                <p className="text-xs font-medium text-gray-900">{getAccessTypeDescription()}</p>
                {assignedClients.length > 0 && (
                  <p className="text-xs text-gray-600">{assignedClients.length} assigned, {allClients.length - assignedClients.length} additional available</p>
                )}
              </div>
            </div>
          </div>

          {/* Search Input */}
          <div className="px-3 py-2 border-b border-gray-200 bg-white">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Type to filter clients..."
                className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="client-search-input"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div className="flex items-center justify-between mt-2">
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const visibleClients = [
                      ...organizedClients.assigned.map(c => c.value),
                      ...organizedClients.others.map(c => c.value)
                    ];
                    const newSelected = new Set([...selectedClients, ...visibleClients]);
                    onSelectionChange(newSelected);
                  }}
                  className="px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  data-testid="select-all-button"
                >
                  Select All
                </button>
                <button
                  onClick={() => {
                    const visibleClients = new Set([
                      ...organizedClients.assigned.map(c => c.value),
                      ...organizedClients.others.map(c => c.value)
                    ]);
                    const newSelected = new Set(
                      Array.from(selectedClients).filter(c => !visibleClients.has(c))
                    );
                    onSelectionChange(newSelected);
                  }}
                  className="px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 rounded transition-colors"
                  data-testid="unselect-all-button"
                >
                  Unselect All
                </button>
              </div>
              {searchTerm && (
                <p className="text-xs text-gray-500">
                  {organizedClients.assigned.length + organizedClients.others.length} results
                </p>
              )}
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {/* Assigned Clients Section */}
            {organizedClients.assigned.length > 0 && (
              <div>
                <div className="sticky top-0 px-3 py-1.5 bg-blue-50 border-b border-blue-100">
                  <span className="text-xs font-semibold text-blue-800 uppercase tracking-wider">
                    Your Assigned Clients ({organizedClients.assigned.length})
                  </span>
                </div>
                {organizedClients.assigned.map((client) => (
                  <label
                    key={client.value}
                    className="flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer group"
                    data-testid={`assigned-client-${client.value}`}
                  >
                    <div className="flex items-center space-x-3 flex-1">
                      <div className={`
                        w-4 h-4 border-2 rounded flex items-center justify-center
                        ${selectedClients.has(client.value)
                          ? 'bg-blue-600 border-blue-600'
                          : 'border-gray-300 group-hover:border-blue-400'
                        }
                      `}>
                        {selectedClients.has(client.value) && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <span className="text-sm text-gray-900">{client.label}</span>
                      <Building2 className="w-3 h-3 text-blue-500 ml-auto" />
                    </div>
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={selectedClients.has(client.value)}
                      onChange={() => handleClientToggle(client.value)}
                    />
                  </label>
                ))}
              </div>
            )}

            {/* Other Clients Section */}
            {organizedClients.others.length > 0 && (
              <div>
                <div className="sticky top-0 px-3 py-1.5 bg-gray-50 border-b border-gray-100">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Other Available Clients ({organizedClients.others.length})
                  </span>
                </div>
                {organizedClients.others.map((client) => (
                  <label
                    key={client.value}
                    className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer group"
                    data-testid={`other-client-${client.value}`}
                  >
                    <div className="flex items-center space-x-3 flex-1">
                      <div className={`
                        w-4 h-4 border-2 rounded flex items-center justify-center
                        ${selectedClients.has(client.value)
                          ? 'bg-gray-600 border-gray-600'
                          : 'border-gray-300 group-hover:border-gray-400'
                        }
                      `}>
                        {selectedClients.has(client.value) && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <span className="text-sm text-gray-700">{client.label}</span>
                    </div>
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={selectedClients.has(client.value)}
                      onChange={() => handleClientToggle(client.value)}
                    />
                  </label>
                ))}
              </div>
            )}

            {/* Empty state */}
            {organizedClients.assigned.length === 0 && organizedClients.others.length === 0 && (
              <div className="px-3 py-8 text-center">
                <Building2 className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">
                  {searchTerm ? `No clients matching "${searchTerm}"` : 'No clients available'}
                </p>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="mt-2 text-xs text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Clear search
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Footer with selection info */}
          {selectedClients.size > 0 && (
            <div className="px-3 py-2 bg-gray-50 border-t border-gray-200">
              <p className="text-xs text-gray-600">
                {selectedClients.size} client{selectedClients.size !== 1 ? 's' : ''} selected
                {assignedClients.length > 0 && ` • ${Array.from(selectedClients).filter(c => assignedClients.includes(c)).length} assigned`}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EnhancedClientFilter;